// ConsultationPackage/PackageOrder.tsx
import React, {useContext, useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {Message} from 'Core/components/Message';
import UserAvatar from 'User/UserAvatar';
import StarRating from 'Core/components/StarRating';
import CircularProgress from 'Core/components/elements/CircularProgress';
import {axios} from 'Auth/axiosConfig';
import {useErrorProcessing} from 'Core/components/ErrorProvider';
import {getConsultationTypeLabel} from 'Locale/ruMap';
import ConsultationOrderPackagePicker from '../ConsultationPackage/ConsultationOrderPackagePicker';
import {redirectWithNextBack} from '../../Utils/redirectNext';
import {ICurrencyWithPrice, IPaymentSystem} from '../../Types/commerce/shop';
import PaymentSelector from "Order/PaymentSelector";
import {useSelector} from 'react-redux';
import {RootState} from "Redux/store";
import {Typography} from '@mui/material';
import moment from 'moment';
import {AuthContext, AuthContextType} from 'Auth/AuthContext';
import {IConsultationConfig, IConsultationsPackage} from "../../Types/psychology/consultation";
import {IPsychologist} from "../../Types/psychology/psychologist";
import {FC, FCC, FR, FRC} from "WideLayout/Layouts";
import BackButton from "Core/components/BackButton";
import {checkClientRequest} from "Consultation/clientRequest";
import {headerHeight} from "Core/components/Header/Header";

export interface ClientRequest {
    age: number;
    age_range: [number, number];
    age_preference: string;
    client_gender: 'male' | 'female' | 'other';
    comfort_preference: 'any' | 'specific';
    consultation_config: IConsultationConfig;
    experienceYears: number | null;
    gender_preference: 'male' | 'female' | 'other' | 'prefer_not_to_say' | '';
    is_psychologist: boolean;
    mental_health: 'excellent' | 'good' | 'normal' | 'bad' | 'terribly' | number | null;
    psychologist: IPsychologist;
    psychologistGender: 'male' | 'female';
    selected_slot: moment.Moment;
    session_time: 'any' | 'nearest' | 'specific';
    skills: number[];
    specializations: number[];
    reservation_paid: number;
    specific_datetime: string | null;
    therapy_experience: boolean;
    is_psychologist_has_child: 'yes' | 'no' | 'not_important';
    timezone: string;
    promocode?: string;
}

const PackageOrder: React.FC = () => {
    const {isAuthenticated} = useContext(AuthContext) as AuthContextType;
    const {skill_categories, status} = useSelector((state: RootState) => state.psychologyParams);
    const {byResponse} = useErrorProcessing();
    const [selectedPackage, setSelectedPackage] = useState<IConsultationsPackage | null>(null);
    const [pickedData, setPickedData] = useState<ClientRequest | null>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [orderCreating, setOrderCreating] = useState<boolean>(false);

    useEffect(() => {
        if (isAuthenticated !== null) {
            if (!isAuthenticated) {
                redirectWithNextBack(navigate, location, '/auth');
                Message.info('Мы сохранили ваш выбор, войдите или зарегистрируйтесь для продолжения.', 1, 9000);
                return;
            }
        }

        const clientRequest = checkClientRequest(true, navigate, location);
        if (clientRequest) {
            setPickedData(clientRequest);
            setLoading(false);
        }
    }, [isAuthenticated, navigate, location]);

    if (loading) {
        return <CircularProgress size={'150px'}/>;
    }

    if (status === 'loading') {
        return <Typography>Загрузка тем для обсуждения...</Typography>;
    }
    if (status === 'failed') {
        return <Typography color="error">Ошибка загрузки тем для обсуждения</Typography>;
    }
    const createOrder = async (
        currency: ICurrencyWithPrice,
        paymentSystem: IPaymentSystem,
        promocode: number | null,
        email?: string,
    ) => {
        if (!selectedPackage) {
            Message.error('Выберите количество консультаций перед оплатой.');
            return;
        }
        if (!currency) {
            Message.error('Выберите валюту.');
            return;
        }
        if (!paymentSystem) {
            Message.error('Выберите способ оплаты.');
            return;
        }
        if (!pickedData?.selected_slot) {
            Message.error('Время, тип или длительность для консультации не были выбраны.', 0, 7000);
            return;
        }
        setOrderCreating(true);
        try {
            const payload: any = {
                product: selectedPackage.id,
                currency: currency.currency,
                payment_system: paymentSystem,
                promocode: promocode ? promocode : null,
                psychologist: pickedData?.psychologist.user.id,
                psychologist_skills: pickedData?.skills,
                has_children: pickedData?.is_psychologist_has_child
                    ? pickedData.is_psychologist_has_child === 'yes'
                        ? true
                        : pickedData.is_psychologist_has_child === 'no'
                            ? false
                            : null
                    : null,
                date_first_consultation: pickedData?.selected_slot.format(),
                consultation_config: pickedData?.consultation_config.id,
                mental_health: pickedData?.mental_health,
                client_gender: pickedData?.client_gender,
                reservation_paid: pickedData?.reservation_paid,
            };
            if (email) payload.email = email;

            const response = await axios.post('/api/v1/orders/create/', payload);
            Message.success('Заказ успешно создан.');
            window.open(response.data, '_blank');
            navigate(`/orders`);
        } catch (error) {
            byResponse(error);
            setOrderCreating(false);
        }
    };

    if (!pickedData) {
        return <div>Не удалось загрузить данные клиента.</div>;
    }

    return (
        <FC maxH={`calc(100vh - ${headerHeight}px)`}>
            <BackButton onClick={() => navigate(`/psychologists?choosed=${pickedData?.psychologist?.id}`)}/>
            <FRC wrap mt={1} g={2} scroll={'y-scroll'} maxH={'100%'}>
                <FC g={1}>
                    <FC g={1} maxW={340}>
                        <FR g={2}>
                            <FC h={'min-content'} pos={'relative'}>
                                <UserAvatar
                                    size={'7rem'}
                                    roundedClassName={'rounded-3'}
                                    avatar={pickedData?.psychologist?.user.avatar}
                                />
                                <StarRating
                                    rating={pickedData?.psychologist?.rating}
                                    starClassName={'fs-6'}
                                    ratingWrapperClassName={'position-absolute z-5 bottom-0 left-0 w-100'}
                                    ratingContentClassName={`frcc px-2 w-100 backdrop-blur-10 rounded-2`}
                                    ratingValueClassName={`d-none`}
                                />
                            </FC>
                            <FCC grow>
                                <h5 className={'text-nowrap enable-tap-select-all'}>
                                    {pickedData?.psychologist?.user.first_name + ' ' + pickedData?.psychologist?.user.last_name}
                                </h5>
                                <span>
                                    {pickedData?.selected_slot ? pickedData.selected_slot.format('DD-MM HH:mm') : ''}
                                </span>
                                <span>
                                    Тип: {getConsultationTypeLabel(pickedData?.consultation_config?.type.name)}
                                </span>
                                <span>Длительность: {pickedData?.consultation_config?.duration.value} минут</span>
                                <p className={'m-0'}>
                                    Запрос:{' '}
                                    {pickedData?.skills
                                        ?.map((id) =>
                                            skill_categories
                                                .flatMap((cat) => cat.skills)
                                                .find((skill) => skill.id === id)?.name)
                                        .filter((name) => name)
                                        .join(', ')}
                                </p>
                                {pickedData?.is_psychologist_has_child && <p>Психолог имеет детей</p>}
                            </FCC>
                        </FR>
                    </FC>
                </FC>

                <FC g={1} minW={270} px={1}>
                    {pickedData?.consultation_config?.type && pickedData?.consultation_config?.duration
                        ? <ConsultationOrderPackagePicker
                            consultationConfig={pickedData.consultation_config}
                            onPick={setSelectedPackage}
                        />
                        : <CircularProgress size={'100px'}/>
                    }
                    {selectedPackage && selectedPackage.prices !== undefined
                        ? <PaymentSelector
                            isLoading={orderCreating}
                            onConfirm={(currency, paymentSystem, promocodeId, email) => {
                                createOrder(currency, paymentSystem, promocodeId, email).then();
                            }}
                            product={selectedPackage}
                            psychologistId={pickedData?.psychologist.user.id}
                        />
                        : <CircularProgress size={'100px'}/>
                    }
                </FC>
            </FRC>
        </FC>
    );
};

export default PackageOrder;
